import type { Company, Credit } from "@kanda-libs/ks-frontend-services";
import { StringIndexedObject } from "@kanda-libs/ks-frontend-services/src/types";
import { formatDate, formatDecimal } from "../helpers";
import { LEGACY_FEES } from "./constants";

export const getLegacyFee = (rate: string): number => LEGACY_FEES?.[rate] || 0;

const checkDate = (
  timestamp?: Date,
  cutoffTimestamp = "2022-10-31"
): boolean => {
  const date = new Date(timestamp as Date) as unknown as number;
  const cutoff = new Date(cutoffTimestamp) as unknown as number;
  const difference = cutoff - date;
  return difference > 0;
};

export interface LoanInfo {
  reference: string;
  created: string;
  loan: string;
  state: string;
  tpTakesDeposit: string;
  rate: string;
  legacy: boolean;
  amountToPay: string;
  deposit: string | number;
  total: string | number;
  subsidyPercentage: string | number;
  subsidyCost: string | number;
  subsidyFee: string | number;
  subsidyTotal: string | number;
}

export const getLoanInfo = (
  credit?: Credit,
  company?: Company,
  depositTaken = false,
  formatAsDecimal = true
): LoanInfo => {
  const ref = credit?.id;
  const state = credit?.state;
  const amount = credit?.amount?.amount || 0;
  const deposit = credit?.deposit_value?.amount || 0;

  const created = formatDate(credit?.metadata?.created_at);

  const skipDeposit = depositTaken ? "Yes" : "No";

  // Calculate the total and include the deposit unless the company
  // has opted to skip it
  const total = amount + (skipDeposit === "Yes" ? 0 : deposit);

  // Get the finance details from the credit / finance_option
  const option = credit?.finance_option;
  const fee = option?.fee || 0;

  // Work out the subsidy cost
  const subsidy = Math.round((fee * total) / 10000);
  // Get the charge if there is one present
  const baseCharge = option?.charge || 0;

  // The total fee cost is the max value of min_charge OR the charge + the subsidy
  const totalFeeCost = Math.max(option?.min_charge || 0, baseCharge + subsidy);

  // The amount to pay is the total minus the fee cost
  const amountToPay = formatDecimal(total - totalFeeCost);

  const legacy = checkDate(credit?.metadata?.created_at);

  const unformattedValues = {
    deposit,
    total,
    subsidyPercentage: fee,
    subsidyCost: subsidy,
    subsidyFee: baseCharge,
    subsidyTotal: totalFeeCost,
  };

  const formattedValues = {
    deposit: formatDecimal(deposit),
    total: formatDecimal(total),
    subsidyPercentage: formatDecimal(fee),
    subsidyCost: formatDecimal(subsidy),
    subsidyFee: formatDecimal(baseCharge),
    subsidyTotal: formatDecimal(totalFeeCost),
  };

  return {
    reference: ref || "No ref",
    created: `${created.date} ${created.time}`,
    loan: formatDecimal(amount),
    state: state || "not_submitted",
    tpTakesDeposit: skipDeposit || "No",
    rate: option?.name || "no_option_chosen",
    legacy,
    amountToPay,
    ...(formatAsDecimal ? formattedValues : unformattedValues),
  };
};
